import React, { useState, useRef, useEffect } from "react";
import {
  X,
  Send,
  GitBranch,
  Percent,
  FileSearch,
  FileText,
  Copy,
  Check,
} from "lucide-react";
import OfficialFrameEvidenceForm, {
  admissibleSourcesOf,
  buildOfficialEvidence,
  emptyOfficialEvidence,
  isOfficialEvidenceReady,
  optionRequiresEvidence,
  questionRequiresEvidence,
} from "../audit/OfficialFrameEvidenceForm";

// ──────────────────────────────────────────────
// HsCopilotPanel — loginHS 전용 AI 어시스턴트 (P1)
// 그리드 변환용 AIChatPanel 대신, 세번 분류 업무에 특화:
//   · 품명 입력 → 증거 기반 분류 (EvidenceClassify — 세번+신뢰도+근거)
//   · 현재 화면(세번) 세율·FTA 요약 (hsk-detail)
//   · 유사 선례 검색 (precedents-search hybrid)
// 모든 응답은 기존 onto API 재사용 — 별도 LLM 채팅 없음 (P2에서 상담대화).
// ──────────────────────────────────────────────

const fmtHsk = (h) =>
  h && h.length === 10 ? `${h.slice(0, 4)}.${h.slice(4, 6)}-${h.slice(6)}` : h;

function readCtx() {
  const p = new URLSearchParams(window.location.search);
  return {
    hsk: p.get("hsk"),
    hs: p.get("hs"),
    chapter: p.get("chapter"),
    section: p.get("section"),
  };
}

function navigateToHsk(hsk) {
  const u = new URL(window.location.href);
  u.searchParams.set("audit", "onto-detail");
  u.searchParams.set("hsk", hsk);
  u.searchParams.set("hs", String(hsk).slice(0, 4));
  u.searchParams.delete("chapter");
  u.searchParams.delete("section");
  window.history.pushState({}, "", u.toString());
  window.dispatchEvent(new PopStateEvent("popstate"));
  // 모바일 — 패널이 전체 화면을 덮어 이동이 안 보임 → 패널이 듣고 자동 닫기
  window.dispatchEvent(new CustomEvent("hsCopilotNavigated"));
}

const CONF_LABEL = {
  high: { text: "높음 — 즉시 확정 수준", color: "#137333" },
  medium: { text: "중간 — 확인 권장", color: "#b06000" },
};

const HsCopilotPanel = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [ctx, setCtx] = useState(readCtx);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const sessionRef = useRef(null); // 결정트리 상담 세션 (P2)
  const lastKeywordRef = useRef(""); // 의견서 초안의 "대상 물품" (P3)
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    const onPop = () => setCtx(readCtx());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // "화면에서 근거 보기" 등 이동 시 — 모바일은 패널이 화면 전체를 덮으므로 닫아서
  // 이동한 화면이 보이게 (데스크톱은 좌측이 보이므로 유지)
  useEffect(() => {
    const onNav = () => {
      if (window.innerWidth < 768 && onClose) onClose();
    };
    window.addEventListener("hsCopilotNavigated", onNav);
    return () => window.removeEventListener("hsCopilotNavigated", onNav);
  }, [onClose]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen]);

  const push = (msg) => setMessages((prev) => [...prev, msg]);

  // ── 액션 1: 증거 기반 분류 ──
  const runClassify = async (keyword) => {
    lastKeywordRef.current = keyword;
    push({ role: "user", text: `"${keyword}" 분류해줘` });
    setLoading(true);
    try {
      const r = await fetch("/api/onto/action/EvidenceClassify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keyword }),
      });
      const d = await r.json();
      push({ role: "assistant", type: "classify", keyword, data: d });
      // P2: 증거 미확정 — 결정트리 상담 세션을 열어 질문으로 좁혀감
      if (!d?.decided) await startConsult(keyword);
    } catch (e) {
      push({ role: "assistant", text: `분류 실패: ${e.message}` });
    } finally {
      setLoading(false);
    }
  };

  // ── P2: 결정트리 상담 — ClassifyProduct 세션 + 질문/답변 대화 ──
  const startConsult = async (keyword) => {
    try {
      const r = await fetch("/api/onto/action/ClassifyProduct", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keyword }),
      });
      if (!r.ok) return;
      const d = await r.json();
      sessionRef.current = d.session_id;
      pushConsultStep(d, true);
    } catch (e) {
      console.warn("[copilot consult]", e.message);
    }
  };

  const pushConsultStep = (d, isFirst = false) => {
    if (d.is_terminal && d.terminal_hsk) {
      push({ role: "assistant", type: "terminal", hsk: d.terminal_hsk, confirmed: false });
    } else if (d.requires_review && !d.next_question) {
      // A hold is a result, not an absence of one. Falling through to the candidate list here
      // rendered the unresolved case as if it were a classification.
      push({
        role: "assistant",
        type: "unresolved",
        detail: d.unresolved_detail_ko,
        reason: d.unresolved_reason,
        missing: Array.isArray(d.missing_facts) ? d.missing_facts : [],
        candidates: Array.isArray(d.candidates) ? d.candidates.slice(0, 4) : [],
      });
    } else if (d.next_question) {
      push({
        role: "assistant",
        type: "question",
        intro: isFirst ? "몇 가지만 여쭤보면 더 좁힐 수 있습니다." : undefined,
        q: d.next_question,
        candCount: Array.isArray(d.candidates) ? d.candidates.length : null,
        answeredValue: null,
      });
    } else if (Array.isArray(d.candidates) && d.candidates.length > 0) {
      push({
        role: "assistant",
        type: "classify",
        keyword: "",
        data: {
          decided: null,
          top_candidates: d.candidates.slice(0, 4).map((c) => ({
            hsk: c.hsk,
            name_ko: c.name_ko || c.popum_ko || "",
          })),
        },
      });
    }
  };

  const answerOption = async (msgIndex, q, opt, evidence = null) => {
    if (loading || !sessionRef.current) return;
    // 답한 질문의 칩 비활성화 표시
    setMessages((prev) =>
      prev.map((m, i) =>
        i === msgIndex ? { ...m, answeredValue: opt.value } : m,
      ),
    );
    push({ role: "user", text: cleanOptLabel(opt.label_ko || opt.value) });
    setLoading(true);
    try {
      const r = await fetch("/api/onto/action/AnswerQuestion", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionRef.current,
          property_key: q.property_key,
          option_value: opt.value,
          ...(evidence ? { evidence } : {}),
        }),
      });
      const d = await r.json();
      if (r.ok) pushConsultStep(d);
      else
        push({
          role: "assistant",
          text: d.error || "세션이 만료되었습니다. 품명을 다시 입력해주세요.",
        });
    } catch (e) {
      push({ role: "assistant", text: `답변 처리 실패: ${e.message}` });
    } finally {
      setLoading(false);
    }
  };

  const acceptTerminal = async (msgIndex, hsk) => {
    if (loading || !sessionRef.current || !hsk) return;
    setLoading(true);
    try {
      const r = await fetch("/api/onto/action/AcceptHSK", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, hsk }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setMessages((prev) =>
        prev.map((m, i) => (i === msgIndex ? { ...m, confirmed: true } : m)),
      );
    } catch (e) {
      push({ role: "assistant", text: `최종 세번 저장 실패: ${e.message}` });
    } finally {
      setLoading(false);
    }
  };

  // ── 액션 2: 현재 세번 세율·FTA ──
  const runRates = async () => {
    if (!ctx.hsk) {
      push({
        role: "assistant",
        text: "지금 보고 있는 세번이 없습니다. 분류 결과나 표에서 세번(10자리)을 먼저 선택해주세요.",
      });
      return;
    }
    push({ role: "user", text: `${fmtHsk(ctx.hsk)} 세율·FTA 알려줘` });
    setLoading(true);
    try {
      const r = await fetch(`/api/onto/hsk-detail?hsk=${ctx.hsk}&lang=kor`);
      const d = await r.json();
      push({ role: "assistant", type: "rates", hsk: ctx.hsk, data: d });
    } catch (e) {
      push({ role: "assistant", text: `세율 조회 실패: ${e.message}` });
    } finally {
      setLoading(false);
    }
  };

  // ── 액션 3: 유사 선례 ──
  const runPrecedents = async (q) => {
    push({ role: "user", text: `"${q}" 유사 선례 보여줘` });
    setLoading(true);
    try {
      const r = await fetch(
        `/api/onto/precedents-search?q=${encodeURIComponent(q)}&mode=hybrid&limit=5`,
      );
      const d = await r.json();
      push({ role: "assistant", type: "precedents", q, data: d });
    } catch (e) {
      push({ role: "assistant", text: `선례 검색 실패: ${e.message}` });
    } finally {
      setLoading(false);
    }
  };

  // ── P3: 분류 의견서 초안 — 세율·선례를 모아 Gemini 작성 ──
  const runOpinion = async () => {
    if (!ctx.hsk) {
      push({
        role: "assistant",
        text: "의견서는 세번(10자리) 화면에서 작성할 수 있습니다. 분류를 먼저 진행해주세요.",
      });
      return;
    }
    const hsk = ctx.hsk;
    push({ role: "user", text: `${fmtHsk(hsk)} 분류 의견서 초안 작성해줘` });
    setLoading(true);
    try {
      // 근거 수집 — 이미 검증된 조회 API 재사용
      const [detR, precR] = await Promise.all([
        fetch(`/api/onto/hsk-detail?hsk=${hsk}&lang=kor`),
        fetch(
          `/api/onto/precedents-search?q=${encodeURIComponent(
            lastKeywordRef.current || hsk.slice(0, 4),
          )}&hs4=${hsk.slice(0, 4)}&mode=hybrid&limit=5`,
        ),
      ]);
      const det = detR.ok ? await detR.json() : {};
      const prec = precR.ok ? await precR.json() : {};
      const g = det.rates_grouped || {};
      const parts = [];
      if (g.basic) parts.push(`기본세율 ${g.basic.tariff_rate}%`);
      const ftaRows = [];
      Object.values(g.fta || {}).forEach((items) =>
        (items || []).forEach((it) => {
          const rate = parseFloat(it.tariff_rate);
          if (!Number.isNaN(rate))
            ftaRows.push(`${it.name_ko || it.rate_code} ${rate}%`);
        }),
      );
      if (ftaRows.length)
        parts.push(`협정세율: ${ftaRows.slice(0, 5).join(", ")}`);
      const r = await fetch("/api/onto/action/OpinionDraft", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          keyword: lastKeywordRef.current,
          hsk,
          name_ko: det.name_ko || det.popum_ko || "",
          rates_summary: parts.join(" / "),
          precedents: (prec.items || []).slice(0, 5).map((p) => ({
            case_id: p.case_id,
            label: p.label,
            reasoning: p.reasoning || p.desc,
          })),
        }),
      });
      const d = await r.json();
      if (r.ok && d.draft) {
        push({ role: "assistant", type: "opinion", hsk, draft: d.draft });
      } else {
        push({ role: "assistant", text: d.error || "의견서 생성 실패" });
      }
    } catch (e) {
      push({ role: "assistant", text: `의견서 생성 실패: ${e.message}` });
    } finally {
      setLoading(false);
    }
  };

  // ── 자유 입력 라우팅 — 단순 의도 분기 (P1) ──
  const handleSend = () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    if (/의견서|보고서/.test(text) && ctx.hsk) {
      runOpinion();
    } else if (/세율|관세율|협정세율|fta/i.test(text) && ctx.hsk) {
      runRates();
    } else if (/선례|사례|판례/.test(text)) {
      const q = text
        .replace(/(의|와|과)?\s*(유사)?\s*(선례|사례|판례).*$/, "")
        .trim();
      runPrecedents(q || text);
    } else {
      runClassify(text);
    }
  };

  if (!isOpen) return null;

  const ctxLabel = ctx.hsk
    ? `세번 ${fmtHsk(ctx.hsk)}`
    : ctx.hs
      ? `제${ctx.hs}호`
      : ctx.chapter
        ? `제${parseInt(ctx.chapter, 10)}류`
        : ctx.section
          ? `제${ctx.section}부`
          : null;

  return (
    <div
      style={{
        width: isMobile ? "100vw" : 400,
        height: "100%",
        background: "#fff",
        borderLeft: isMobile ? "none" : "1px solid #e5e7eb",
        boxShadow: "-4px 0 24px rgba(0,0,0,0.10)",
        display: "flex",
        flexDirection: "column",
        fontFamily:
          "'Malgun Gothic', '맑은 고딕', 'Pretendard', -apple-system, 'Segoe UI', sans-serif",
      }}
    >
      {/* 헤더 */}
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid #ecedf2",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span style={{ fontSize: 14.5, fontWeight: 800, color: "#16151a" }}>
          AI 어시스턴트
        </span>
        <span style={{ fontSize: 12, color: "#9ca3af" }}>세번 분류</span>
        {ctxLabel && (
          <span
            style={{
              marginLeft: 4,
              fontSize: 11.5,
              padding: "2px 8px",
              borderRadius: 999,
              background: "#f1f3f8",
              color: "#4b5563",
            }}
          >
            {ctxLabel}
          </span>
        )}
        <button
          onClick={onClose}
          style={{
            marginLeft: "auto",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "#6b7280",
            padding: 4,
          }}
        >
          <X size={18} />
        </button>
      </div>

      {/* 빠른 액션 */}
      <div
        style={{
          padding: "10px 16px",
          borderBottom: "1px solid #ecedf2",
          display: "flex",
          gap: 6,
          flexWrap: "wrap",
        }}
      >
        <QuickBtn
          Icon={GitBranch}
          label="품목 분류"
          onClick={() => {
            const t = input.trim();
            if (t) {
              setInput("");
              runClassify(t);
            } else {
              inputRef.current?.focus();
              push({
                role: "assistant",
                text: "분류할 품명을 아래에 입력하고 보내주세요 — 예: 전동 킥보드, 가죽 핸드백, 비타민C 분말",
              });
            }
          }}
        />
        <QuickBtn
          Icon={Percent}
          label="세율·FTA"
          disabled={!ctx.hsk}
          title={ctx.hsk ? "" : "세번(10자리) 화면에서 사용 가능"}
          onClick={runRates}
        />
        <QuickBtn
          Icon={FileText}
          label="의견서 초안"
          disabled={!ctx.hsk}
          title={ctx.hsk ? "" : "세번(10자리) 화면에서 사용 가능"}
          onClick={runOpinion}
        />
        <QuickBtn
          Icon={FileSearch}
          label="유사 선례"
          onClick={() => {
            const t = input.trim();
            if (t) {
              setInput("");
              runPrecedents(t);
            } else {
              inputRef.current?.focus();
              push({
                role: "assistant",
                text: "선례를 찾을 품명·키워드를 아래에 입력하고 보내주세요.",
              });
            }
          }}
        />
      </div>

      {/* 메시지 영역 */}
      <div style={{ flex: 1, overflowY: "auto", padding: "14px 16px" }}>
        {messages.length === 0 && (
          <div style={{ color: "#6b7280", fontSize: 13, lineHeight: 1.7 }}>
            <p
              style={{ margin: "0 0 10px", fontWeight: 700, color: "#374151" }}
            >
              무엇을 도와드릴까요?
            </p>
            <p style={{ margin: 0 }}>
              · 품명을 입력하면 <b>세번(10자리)과 근거</b>를 찾아드립니다
              <br />· 세번 화면에선 <b>세율·FTA</b>를 바로 요약합니다
              <br />· "○○ 선례 보여줘"로 <b>분류사례</b>를 검색합니다
            </p>
          </div>
        )}
        {messages.map((m, i) =>
          m.role === "user" ? (
            <UserBubble key={i} text={m.text} />
          ) : (
            <AssistantBlock
              key={i}
              msg={m}
              onAnswer={(q, opt, evidence) => answerOption(i, q, opt, evidence)}
              onAccept={(hsk) => acceptTerminal(i, hsk)}
            />
          ),
        )}
        {loading && (
          <div style={{ color: "#9ca3af", fontSize: 12.5, padding: "6px 0" }}>
            증거 검토 중…
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* 입력 */}
      <div
        style={{
          padding: "10px 12px calc(10px + env(safe-area-inset-bottom))",
          borderTop: "1px solid #ecedf2",
          display: "flex",
          gap: 8,
        }}
      >
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.nativeEvent.isComposing) handleSend();
          }}
          placeholder="품명 입력 — 예: 전동 킥보드, 가죽 핸드백"
          style={{
            flex: 1,
            border: "1px solid #dadce0",
            borderRadius: 10,
            padding: "10px 12px",
            fontSize: 13.5,
            outline: "none",
          }}
        />
        <button
          onClick={handleSend}
          disabled={loading || !input.trim()}
          style={{
            border: "none",
            borderRadius: 10,
            padding: "0 14px",
            background: input.trim() && !loading ? "#1b1a20" : "#e5e7eb",
            color: input.trim() && !loading ? "#fff" : "#9ca3af",
            cursor: input.trim() && !loading ? "pointer" : "default",
          }}
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
};

// ─── 서브 컴포넌트 ───────────────────────────────────────

function QuickBtn({ Icon, label, onClick, disabled, title }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title || label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "6px 11px",
        borderRadius: 999,
        border: "1px solid #dadce0",
        background: "#fff",
        fontSize: 12.5,
        color: disabled ? "#c0c4cc" : "#374151",
        cursor: disabled ? "default" : "pointer",
      }}
    >
      <Icon size={13} /> {label}
    </button>
  );
}

function UserBubble({ text }) {
  return (
    <div
      style={{ display: "flex", justifyContent: "flex-end", margin: "8px 0" }}
    >
      <div
        style={{
          maxWidth: "85%",
          background: "#1b1a20",
          color: "#fff",
          borderRadius: "12px 12px 3px 12px",
          padding: "8px 12px",
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        {text}
      </div>
    </div>
  );
}

function AssistantBlock({ msg, onAnswer, onAccept }) {
  if (msg.type === "classify") return <ClassifyResult msg={msg} />;
  if (msg.type === "rates") return <RatesResult msg={msg} />;
  if (msg.type === "precedents") return <PrecedentsResult msg={msg} />;
  if (msg.type === "question")
    return <QuestionBlock msg={msg} onAnswer={onAnswer} />;
  if (msg.type === "terminal") return <TerminalBlock msg={msg} onAccept={onAccept} />;
  if (msg.type === "unresolved") return <UnresolvedBlock msg={msg} />;
  if (msg.type === "opinion") return <OpinionBlock msg={msg} />;
  return (
    <div style={blockStyle}>
      <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}>
        {msg.text}
      </div>
    </div>
  );
}

/** 확정하지 않고 보류한 결과 — 후보 목록만 보여 분류된 것처럼 읽히지 않게 명시한다. */
function UnresolvedBlock({ msg }) {
  return (
    <div style={{ ...blockStyle, border: "1px solid #fcd34d", background: "#fffbeb" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e" }}>
        확정하지 않고 보류했습니다
      </div>
      <div style={{ fontSize: 12.5, color: "#92400e", lineHeight: 1.6, marginTop: 5 }}>
        {msg.detail
          || "남은 후보를 구분할 근거가 없어 임의의 세번으로 확정하지 않았습니다. 관세사 검토가 필요합니다."}
      </div>
      {msg.missing.length > 0 && (
        <div style={{ marginTop: 9 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e" }}>확정에 필요한 자료</div>
          {msg.missing.map((item) => (
            <div key={item.fact_id} style={{ fontSize: 12, color: "#a16207", lineHeight: 1.55, marginTop: 4 }}>
              · {item.question_ko}
              <div style={{ paddingLeft: 10 }}>확인 위치: {item.observable_ko}</div>
            </div>
          ))}
        </div>
      )}
      {msg.candidates.length > 0 && (
        <div style={{ marginTop: 9, fontSize: 12, color: "#78716c", lineHeight: 1.55 }}>
          <div style={{ fontWeight: 700 }}>남은 후보 (확정 아님)</div>
          {msg.candidates.map((c) => (
            <div key={c.hsk}>{c.hsk} {c.name_ko || c.popum_ko || ""}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// 결정트리 옵션 라벨 정리 — 원천 데이터 HTML 태그 제거 + "(세번)" 꼬리 제거
function cleanOptLabel(s) {
  return String(s || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s*\(([0-9]{4,10})[^)]*\)\s*$/, " ($1)")
    .replace(/\s+/g, " ")
    .trim();
}

// ── P2: 결정트리 질문 말풍선 (옵션 칩 — 탭하면 답변) ──
function QuestionBlock({ msg, onAnswer }) {
  const q = msg.q || {};
  const opts = (q.options || [])
    .filter((opt) => opt.confidence !== "statistical")
    .slice(0, 8);
  const answered = msg.answeredValue != null;
  // 공식 프레임 분기는 근거 폼 없이 즉시 제출한다(서버가 selection_only 로 기록).
  const isOfficial = questionRequiresEvidence(q) && q.source !== "official-frame-v2";
  const [evidence, setEvidence] = useState(emptyOfficialEvidence);
  const [selectedOption, setSelectedOption] = useState(null);
  const evidenceReady = !isOfficial || isOfficialEvidenceReady(evidence, selectedOption);
  return (
    <div style={blockStyle}>
      {msg.intro && (
        <div style={{ fontSize: 12.5, color: "#374151", marginBottom: 6 }}>
          {msg.intro}
        </div>
      )}
      <div style={{ fontSize: 13.5, fontWeight: 700, color: "#16151a" }}>
        {q.question_ko || q.display_name_ko || "선택해주세요"}
        {msg.candCount ? (
          <span
            style={{
              fontSize: 11.5,
              fontWeight: 400,
              color: "#9ca3af",
              marginLeft: 6,
            }}
          >
            후보 {msg.candCount}건
          </span>
        ) : null}
      </div>
      {q.decision_basis_ko && (
        <div style={{ marginTop: 5, fontSize: 12, color: "#6b7280", lineHeight: 1.45 }}>
          {q.decision_basis_ko}
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
        {opts.map((opt) => {
          const reachable = opt.is_reachable !== false;
          const isPicked = answered && msg.answeredValue === opt.value;
          const isSelected = selectedOption?.value === opt.value;
          return (
            <button
              key={String(opt.value)}
              disabled={!reachable || answered}
              onClick={() => {
                if (!optionRequiresEvidence(q, opt)) {
                  if (onAnswer) onAnswer(q, opt, null);
                  return;
                }
                if (!isSelected) setEvidence(emptyOfficialEvidence());
                setSelectedOption(opt);
              }}
              style={{
                padding: "6px 11px",
                borderRadius: 999,
                border: "1px solid " + (isPicked || isSelected ? "#1a73e8" : "#dadce0"),
                background: isPicked || isSelected ? "#e8f0fe" : "#fff",
                fontSize: 12.5,
                color: !reachable
                  ? "#c0c4cc"
                  : isPicked || isSelected
                    ? "#1a73e8"
                    : answered
                      ? "#9ca3af"
                      : "#374151",
                cursor: !reachable || answered ? "default" : "pointer",
              }}
            >
              {cleanOptLabel(opt.label_ko || String(opt.value))}
              {opt.statistical_hint ? " 📊" : ""}
            </button>
          );
        })}
      </div>
      {isOfficial && selectedOption && (
        <div style={{ marginTop: 8 }}>
          <OfficialFrameEvidenceForm
            value={evidence}
            onChange={setEvidence}
            disabled={answered}
            compact
            requiredFacts={selectedOption.required_facts || []}
            admissibleSources={admissibleSourcesOf(selectedOption)}
            observableKo={q.observable_ko || ""}
          />
          <button
            type="button"
            disabled={answered || !evidenceReady}
            onClick={() =>
              onAnswer && onAnswer(
                q,
                selectedOption,
                buildOfficialEvidence(evidence, selectedOption),
              )
            }
            style={{
              ...goBtnStyle,
              borderColor: "#1a73e8",
              color: "#1a73e8",
              opacity: evidenceReady ? 1 : 0.55,
              cursor: evidenceReady ? "pointer" : "default",
            }}
          >
            확인한 근거로 이 분기 제출
          </button>
        </div>
      )}
    </div>
  );
}

// ── P2: 결정트리 종착 — 세번 도달 카드 ──
function TerminalBlock({ msg, onAccept }) {
  return (
    <div style={blockStyle}>
      <div style={{ fontSize: 11.5, color: "#9ca3af", marginBottom: 4 }}>
        결정트리 도달
      </div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 800,
          fontFamily: "JetBrains Mono, monospace",
          color: "#16151a",
        }}
      >
        {fmtHsk(msg.hsk)}
      </div>
      <div
        style={{
          fontSize: 12.5,
          color: "#374151",
          marginTop: 4,
          lineHeight: 1.55,
        }}
      >
        답변하신 조건으로 이 세번에 도달했습니다. 화면에서 세율·선례 근거를
        확인하고 확정하세요.
      </div>
      <button
        type="button"
        disabled={msg.confirmed}
        onClick={() => onAccept && onAccept(msg.hsk)}
        style={{
          ...goBtnStyle,
          marginRight: 6,
          borderColor: "#147a4b",
          color: msg.confirmed ? "#667085" : "#147a4b",
          cursor: msg.confirmed ? "default" : "pointer",
        }}
      >
        {msg.confirmed ? "확정·저장 완료" : "이 세번으로 확정·저장"}
      </button>
      <button onClick={() => navigateToHsk(msg.hsk)} style={goBtnStyle}>
        화면에서 근거 보기 →
      </button>
    </div>
  );
}

const blockStyle = {
  margin: "8px 0",
  border: "1px solid #ecedf2",
  borderRadius: 12,
  padding: "10px 12px",
  background: "#fafbfd",
};

function ClassifyResult({ msg }) {
  const d = msg.data || {};
  const dec = d.decided;
  const cands = Array.isArray(d.top_candidates) ? d.top_candidates : [];
  const evid = Array.isArray(d.evidence) ? d.evidence : [];

  if (dec) {
    const conf = CONF_LABEL[dec.confidence] || {
      text: dec.confidence,
      color: "#6b7280",
    };
    return (
      <div style={blockStyle}>
        <div style={{ fontSize: 11.5, color: "#9ca3af", marginBottom: 4 }}>
          증거 기반 분류 결과
        </div>
        <div
          style={{
            fontSize: 16,
            fontWeight: 800,
            fontFamily: "JetBrains Mono, monospace",
            color: "#16151a",
          }}
        >
          {fmtHsk(dec.hsk)}
        </div>
        <div style={{ fontSize: 13, color: "#374151", margin: "2px 0 6px" }}>
          {dec.popum_ko || ""}
        </div>
        <div style={{ fontSize: 12, color: conf.color, fontWeight: 700 }}>
          신뢰도: {conf.text}
        </div>
        {evid.length > 0 && (
          <div
            style={{
              marginTop: 6,
              fontSize: 12,
              color: "#6b7280",
              lineHeight: 1.6,
            }}
          >
            {evid.slice(0, 3).map((e, i) => (
              <div key={i}>
                · {sourceLabel(e.source)} {e.count}건
                {e.majority_ratio
                  ? ` 중 ${Math.round(e.majority_ratio * 100)}% 일치`
                  : ""}
              </div>
            ))}
          </div>
        )}
        <button onClick={() => navigateToHsk(dec.hsk)} style={goBtnStyle}>
          화면에서 근거 보기 →
        </button>
      </div>
    );
  }

  if (cands.length > 0) {
    return (
      <div style={blockStyle}>
        <div
          style={{
            fontSize: 12.5,
            color: "#374151",
            marginBottom: 8,
            lineHeight: 1.55,
          }}
        >
          증거가 갈려 확정하지 않았습니다 — 가능성 높은 순서로 후보를
          보여드립니다. 눌러서 근거를 확인하세요.
        </div>
        {cands.slice(0, 4).map((c) => (
          <button
            key={c.hsk}
            onClick={() => navigateToHsk(c.hsk)}
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              background: "#fff",
              padding: "8px 10px",
              marginBottom: 6,
              cursor: "pointer",
            }}
          >
            <span
              style={{
                fontFamily: "JetBrains Mono, monospace",
                fontWeight: 700,
                fontSize: 13,
                color: "#16151a",
              }}
            >
              {fmtHsk(c.hsk)}
            </span>
            <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 8 }}>
              {c.name_ko || ""}
            </span>
            {typeof c.ratio === "number" && (
              <span style={{ fontSize: 11.5, color: "#9ca3af", marginLeft: 6 }}>
                유사도 {Math.round(c.ratio * 100)}%
              </span>
            )}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div style={blockStyle}>
      <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}>
        "{msg.keyword}" 에 대한 증거를 충분히 찾지 못했습니다. 재질·용도를
        붙여서 다시 시도해보세요 — 예: "가죽 핸드백", "플라스틱 물병".
      </div>
    </div>
  );
}

function sourceLabel(s) {
  const map = {
    precedent_exact: "분류사례(정확)",
    precedent_partial: "분류사례",
    precedent: "분류사례",
    master: "관세율표",
    embedding: "유사사례(의미)",
    gemini: "AI 검토",
    user_feedback: "사용자 확정 이력",
  };
  return map[s] || s;
}

function RatesResult({ msg }) {
  const d = msg.data || {};
  const g = d.rates_grouped || {};
  const basic = g.basic;
  const fta = g.fta || {};
  const ftaRows = [];
  Object.entries(fta).forEach(([group, items]) => {
    (items || []).forEach((it) => {
      const rate = parseFloat(it.tariff_rate);
      if (!Number.isNaN(rate))
        ftaRows.push({ group, name: it.name_ko || it.rate_code, rate });
    });
  });
  ftaRows.sort((a, b) => a.rate - b.rate);

  return (
    <div style={blockStyle}>
      <div style={{ fontSize: 11.5, color: "#9ca3af", marginBottom: 4 }}>
        {fmtHsk(msg.hsk)} 세율 요약
      </div>
      {basic ? (
        <div style={{ fontSize: 13.5, color: "#16151a", fontWeight: 700 }}>
          기본세율 {basic.tariff_rate}%
          {basic.name_ko ? (
            <span style={{ fontWeight: 400, color: "#6b7280", fontSize: 12 }}>
              {" "}
              ({basic.name_ko})
            </span>
          ) : null}
        </div>
      ) : (
        <div style={{ fontSize: 13, color: "#6b7280" }}>기본세율 정보 없음</div>
      )}
      {ftaRows.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11.5, color: "#9ca3af", marginBottom: 3 }}>
            협정세율 낮은 순 (원산지 증빙 필요)
          </div>
          {ftaRows.slice(0, 5).map((r, i) => (
            <div
              key={i}
              style={{ fontSize: 12.5, color: "#374151", lineHeight: 1.7 }}
            >
              · {r.name} — <b>{r.rate}%</b>
            </div>
          ))}
        </div>
      )}
      <button onClick={() => navigateToHsk(msg.hsk)} style={goBtnStyle}>
        전체 세율·요건 보기 →
      </button>
    </div>
  );
}

function PrecedentsResult({ msg }) {
  const items = (msg.data && msg.data.items) || [];
  if (!items.length) {
    return (
      <div style={blockStyle}>
        <div style={{ fontSize: 13, color: "#374151" }}>
          "{msg.q}" 관련 선례를 찾지 못했습니다. 다른 표현으로 시도해보세요.
        </div>
      </div>
    );
  }
  return (
    <div style={blockStyle}>
      <div style={{ fontSize: 11.5, color: "#9ca3af", marginBottom: 6 }}>
        "{msg.q}" 유사 선례 {msg.data.count ?? items.length}건 중 상위{" "}
        {Math.min(items.length, 5)}건
      </div>
      {items.slice(0, 5).map((it, i) => {
        const hsk = (it.hsk || it.hsk_code || "").replace(/[^0-9]/g, "");
        return (
          <div
            key={i}
            onClick={() => hsk.length === 10 && navigateToHsk(hsk)}
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              background: "#fff",
              padding: "8px 10px",
              marginBottom: 6,
              cursor: hsk.length === 10 ? "pointer" : "default",
            }}
          >
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "#16151a" }}>
              {it.label || it.case_id}
              {hsk.length === 10 && (
                <span
                  style={{
                    fontFamily: "JetBrains Mono, monospace",
                    fontWeight: 600,
                    fontSize: 11.5,
                    color: "#1a73e8",
                    marginLeft: 6,
                  }}
                >
                  {fmtHsk(hsk)}
                </span>
              )}
            </div>
            {it.desc && (
              <div
                style={{
                  fontSize: 11.5,
                  color: "#6b7280",
                  marginTop: 2,
                  lineHeight: 1.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {it.desc}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── P3: 의견서 초안 블록 — 마크다운 라이트 렌더 + 복사 ──
function OpinionBlock({ msg }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(msg.draft);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) {
      /* clipboard 권한 없음 — 무시 */
    }
  };
  return (
    <div style={blockStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: 6,
        }}
      >
        <span style={{ fontSize: 11.5, color: "#9ca3af" }}>
          {fmtHsk(msg.hsk)} 분류 의견서 초안
        </span>
        <button
          onClick={copy}
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            border: "1px solid #dadce0",
            borderRadius: 6,
            background: "#fff",
            padding: "3px 8px",
            fontSize: 11.5,
            color: copied ? "#137333" : "#374151",
            cursor: "pointer",
          }}
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
          {copied ? "복사됨" : "복사"}
        </button>
      </div>
      <div style={{ fontSize: 12.5, color: "#374151", lineHeight: 1.7 }}>
        {String(msg.draft)
          .split("\n")
          .map((line, i) => {
            const h = line.match(/^#{1,3}\s*(.+)$/);
            if (h)
              return (
                <div
                  key={i}
                  style={{
                    fontWeight: 700,
                    color: "#16151a",
                    marginTop: i === 0 ? 0 : 10,
                    marginBottom: 2,
                  }}
                >
                  {h[1]}
                </div>
              );
            return (
              <div key={i} style={{ minHeight: line.trim() ? undefined : 6 }}>
                {line.replace(/\*\*/g, "")}
              </div>
            );
          })}
      </div>
    </div>
  );
}

const goBtnStyle = {
  marginTop: 8,
  border: "1px solid #dadce0",
  borderRadius: 8,
  background: "#fff",
  padding: "6px 10px",
  fontSize: 12,
  fontWeight: 600,
  color: "#1a73e8",
  cursor: "pointer",
};

export default HsCopilotPanel;
